import React, { useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import images from "../../app/utlis/image";
import { getPageData, useData } from "../../app/helper/functions";
import MoodyMonthAppContent from "../organisms/ProjectsList/MoodyMonthProject/MoodyMonthAppContent";
import MoodyMonthAppOverview from "../organisms/ProjectsList/MoodyMonthProject/MoodyMonthAppOverview";
import MoodyAppSteps from "../organisms/ProjectsList/MoodyMonthProject/MoodyAppSteps";
import AppsTestimonials from "../molecules/AppsTestimonials/AppsTestimonials";
import MOCKUP1 from "../../app/assets/images/projectImage/moodyMonthImage/Mockup1.webp"
import MOCKUP2 from "../../app/assets/images/projectImage/moodyMonthImage/Mockup2.webp"
import MOCKUP3 from "../../app/assets/images/projectImage/moodyMonthImage/Mockup3.webp"
import MOCKUP4 from "../../app/assets/images/projectImage/moodyMonthImage/Mockup4.webp"
import CompanyLogoImage from "../molecules/CompanyLogoImage/CompanyLogoImage";
import ContactHelp from "../molecules/ContactHelp/ContactHelp";
import Footer from "../organisms/Footer/Footer";


const MoodyMonthAppPage = () => {
    const { data } = useData();
    const moodyProjectData = getPageData(data, 'moodyProjectPage');
    const { scrollYProgress } = useScroll();

    // Create scroll-based animations
    const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.8]);
    const headerScale = useTransform(scrollYProgress, [0, 0.1], [1, 0.95]);

    // Create refs for scroll-triggered animations
    const titleRef = React.useRef(null);
    const contentRef = React.useRef(null);
    const overviewRef = React.useRef(null);
    const mockupRef = React.useRef(null);
    const summaryRef = React.useRef(null);
    const logoRef = React.useRef(null);

    // Use inView hooks for scroll animation triggers
    const isTitleInView = useInView(titleRef, { once: false, amount: 0.3 });
    const isContentInView = useInView(contentRef, { once: false, amount: 0.2 });
    const isOverviewInView = useInView(overviewRef, { once: false, amount: 0.2 });
    const isMockupInView = useInView(mockupRef, { once: false, amount: 0.1 });
    const isSummaryInView = useInView(summaryRef, { once: false, amount: 0.1 });
    const isLogoInView = useInView(logoRef, { once: false, amount: 0.3 });

    // Staggered animation variants
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.3
            }
        }
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    // Fade up animation variants
    const fadeUpVariant = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
    };

    return (
        <div>
            <div className="">
                <div className="bg-custom-gradient">
                    <div className="bg-blackTransparent">
                        <motion.div
                            className="flex flex-col justify-center items-center px-4 sm:px-8 md:px-16 pt-8 md:pt-16 pb-0"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8 }}
                        >
                            <motion.div
                                className="w-full text-center text-[3rem] sm:text-[4rem] md:text-[6rem] lg:text-[8rem] xl:text-[12rem] font-bold relative -bottom-10 sm:-bottom-20"
                                style={{
                                    background: 'linear-gradient(180.06deg, rgba(55, 185, 171, 0.25) 4.55%, rgba(55, 185, 171, 0) 79.14%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    opacity: headerOpacity,
                                    scale: headerScale
                                }}
                                initial={{ y: 50, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                transition={{ duration: 1.2, ease: "easeOut" }}
                            >
                                Moody Month
                            </motion.div>
                            <motion.div
                                className="flex justify-center items-center p-4 sm:p-8 md:p-12 py-0 !pb-0 overflow-hidden"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 1, delay: 0.3 }}
                            >
                                <div className="relative max-w-[1000px] flex justify-center items-center">
                                    <motion.img
                                        src={images.MOODYAPPBANNERIMAGE}
                                        className="w-[90%] sm:w-[80%] h-auto"
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ duration: 0.3 }}
                                    />
                                </div>
                            </motion.div>
                        </motion.div>
                        <div className='w-full' style={{
                            borderBottom: "2px solid transparent",
                            borderImageSource:
                                `linear-gradient(90deg, rgb(49, 49, 49) 0%, rgb(55, 185, 171) 51%, rgb(49, 49, 49) 92%, rgb(49, 49, 49) 96%)`,
                            borderImageSlice: 1,
                        }}></div>
                    </div>
                </div>
                <div className="bg-darkGreyPrimary">
                    <motion.div
                        ref={titleRef}
                        className="text-white flex flex-col gap-2 relative px-4 sm:pl-[9%] text-center pt-8 md:pt-12"
                        variants={fadeUpVariant}
                        initial="hidden"
                        animate={isTitleInView ? "visible" : "hidden"}
                    >
                        <motion.div
                            className="capitalize font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } }
                            }}
                        >
                            Moody Month
                        </motion.div>
                        <motion.div
                            className="font-medium text-lg sm:text-xl md:text-2xl"
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.4 } }
                            }}
                        >
                            Optimizing patient care: How we developed a website
                            <br className="hidden sm:block" />
                            for excellent care clinics
                        </motion.div>
                    </motion.div>

                    <div ref={contentRef}>
                        <MoodyMonthAppContent moodyProjectData={moodyProjectData} />
                    </div>

                    <div ref={overviewRef}>
                        <MoodyMonthAppOverview />
                    </div>

                    <MoodyAppSteps moodyProjectData={moodyProjectData} />

                    <motion.div
                        className='relative p-4 sm:p-8 md:p-16 lg:p-24 bg-moody-gradient-4'
                        ref={mockupRef}
                        variants={container}
                        initial="hidden"
                        animate={isMockupInView ? "show" : "hidden"}
                    >
                        <div className="flex flex-wrap md:flex-nowrap justify-center md:justify-between items-center gap-4 md:gap-5 relative -top-4 md:-top-10">
                            <motion.img
                                variants={item}
                                src={MOCKUP1}
                                className='w-[45%] md:w-[22%] mb-4 md:mb-0'
                                animate={{ y: [0, -10, 0] }}
                                transition={{
                                    // repeat: Infinity, 
                                    duration: 3,
                                    ease: "easeInOut"
                                }}
                                whileHover={{
                                    y: -20,
                                    transition: { duration: 0.2, ease: "easeOut" }
                                }}
                            />
                            <div className="w-[45%] md:w-[22%] relative md:!translate-y-[15%] h-full">
                                <motion.img
                                    variants={item}
                                    src={MOCKUP2}
                                    className='  mb-4 md:mb-0'
                                    animate={{ y: [0, -15, 0] }}
                                    transition={{
                                        // repeat: Infinity, 
                                        duration: 3.5,
                                        delay: 0.5,
                                        ease: "easeInOut"
                                    }}
                                    whileHover={{
                                        y: -25,
                                        transition: { duration: 0.2, ease: "easeOut" }
                                    }}
                                />
                            </div>
                            <div className="w-[45%] md:w-[22%] relative md:!translate-y-[6%] h-full">
                                <motion.img
                                    variants={item}
                                    src={MOCKUP3}
                                    className=' mb-4 md:mb-0'
                                    animate={{ y: [0, -12, 0] }}
                                    transition={{
                                        // repeat: Infinity, 
                                        duration: 3.2,
                                        delay: 1,
                                        ease: "easeInOut"
                                    }}
                                    whileHover={{
                                        y: -25,
                                        transition: { duration: 0.2, ease: "easeOut" }
                                    }}
                                />
                            </div>
                            <div className="w-[45%] md:w-[22%] md:translate-y-[15%]">
                                <motion.img
                                    variants={item}
                                    src={MOCKUP4}
                                    className=''
                                    animate={{ y: [0, -10, 0] }}
                                    transition={{
                                        // repeat: Infinity, 
                                        duration: 2.8,
                                        delay: 1.5,
                                        ease: "easeInOut"
                                    }}
                                    whileHover={{
                                        y: -20,
                                        transition: { duration: 0.2, ease: "easeOut" }
                                    }}
                                />
                            </div>

                        </div>
                    </motion.div>

                    <motion.div
                        ref={summaryRef}
                        className='pt-10 md:pt-20 text-center flex flex-col items-center justify-center gap-4 pb-8 md:pb-16 px-4 sm:px-6'
                        variants={fadeUpVariant}
                        initial="hidden"
                        animate={isSummaryInView ? "visible" : "hidden"}
                    >
                        <motion.div
                            className='text-2xl sm:text-3xl md:text-4xl font-bold'
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.2 } }
                            }}
                        >
                            Moody Month
                        </motion.div>
                        <motion.div
                            className='w-full sm:w-[80%] md:w-[70%] lg:w-[60%]'
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.4 } }
                            }}
                        >
                            The website is now easier to use because we divided it into sections for different pain types.
                            Visitors can easily look through each category to find the right treatments. Finding information
                            about specific pain conditions and treatments is simpler, making the site more user-friendly.
                            We aim to help people make smart choices for pain management, so we provide clear information
                            about available treatments in each section, assisting visitors in making the best decisions for themselves.
                        </motion.div>
                    </motion.div>

                    <AppsTestimonials color="#37B9AB" bgcolor="bg-moodyTestColor" />

                    <motion.div
                        ref={logoRef}
                        className='p-6 sm:p-8 md:p-10 px-4 sm:px-8 md:px-16 lg:px-36'
                        variants={fadeUpVariant}
                        initial="hidden"
                        animate={isLogoInView ? "visible" : "hidden"}
                    >
                        <CompanyLogoImage />
                    </motion.div>

                    <ContactHelp title="Let's connect & achieve your business goals together" buttonTitle='CONTACT US' />
                    <Footer />
                </div>
            </div>
        </div >
    );
};

export default MoodyMonthAppPage;
